import { supabase } from "@/lib/supabase";
import { UserSession } from "@/types/session";
import { ChatEntry } from "@/types/chat";

const SESSION_EXPIRATION_TIME_MS = 1000 * 60 * 60;

class SessionService {
  getOrCreateChatSession = async (chatMode: ChatEntry): Promise<UserSession> => {
    const currentSession = await this.getCurrentChatSession(chatMode);
    if (currentSession) return currentSession;
    return this.createSession(chatMode);
  };

  createNewSingleSession = async (chatMode: ChatEntry): Promise<UserSession> => {
    const currentSession = await this.getCurrentChatSession(chatMode);
    if (currentSession) await this.closeSession(currentSession.id);
    return this.createSession(chatMode);
  };

  /** Closed sessions only — used for post-session feedback prompts. */
  getClosedSessions = async (limit: number = 10): Promise<UserSession[]> => {
    const currentUserId = await this.getCurrentUserId();
    if (!currentUserId) return [];
    const { data, error } = await supabase
      .from("sessions")
      .select("*")
      .eq("user_id", currentUserId)
      .not("closed_at", "is", null)
      .neq("chat_mode", "daily-reset")
      .order("closed_at", { ascending: false })
      .limit(limit);
    if (error) throw error;
    return data;
  };

  /**
   * All conversations that have saved messages — open or closed.
   * Powers the History page so chats appear as soon as the first turn is persisted.
   */
  getHistorySessions = async (limit: number = 40): Promise<UserSession[]> => {
    const currentUserId = await this.getCurrentUserId();
    if (!currentUserId) return [];
    const { data, error } = await supabase
      .from("sessions")
      .select("*, session_chat_histories!inner(id)")
      .eq("user_id", currentUserId)
      .neq("chat_mode", "daily-reset")
      .order("created_at", { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data ?? []).map(stripHistoryJoinRow);
  };

  rateSessions = async (sessionIds: UserSession["id"][], rating: 0 | 1): Promise<UserSession[]> => {
    if (sessionIds.length === 0) throw new Error("No session ids provided");

    const { data, error } = await supabase
      .from("sessions")
      .update({ rating, rated_at: new Date().toISOString() })
      .in("id", sessionIds)
      .select();

    if (error) throw error;
    return data;
  };

  extendSession = async (sessionId: UserSession["id"]): Promise<UserSession> => {
    const { data, error } = await supabase
      .from("sessions")
      .update({ expires_at: new Date(Date.now() + SESSION_EXPIRATION_TIME_MS).toISOString() })
      .eq("id", sessionId)
      .select()
      .single();
    if (error) throw error;
    return data;
  };

  getCurrentChatSession = async (chatMode: ChatEntry): Promise<UserSession | null> => {
    const currentUserId = await this.getCurrentUserId();
    if (!currentUserId) return null;
    const { data, error } = await supabase
      .from("sessions")
      .select("*")
      .eq("user_id", currentUserId)
      .is("closed_at", null)
      .eq("chat_mode", chatMode)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data;
  };

  getSessionById = async (sessionId: UserSession["id"]): Promise<UserSession | null> => {
    const { data, error } = await supabase.from("sessions").select("*").eq("id", sessionId).single();
    if (error) throw error;
    return data;
  };

  getClosedUnratedSessions = async (closeExpired: boolean = false): Promise<UserSession[]> => {
    const currentUserId = await this.getCurrentUserId();
    if (!currentUserId) return [];
    const { data: unratedSessions, error } = await supabase
      .from("sessions")
      .select("*")
      .eq("user_id", currentUserId)
      .is("rated_at", null);

    if (error) throw error;

    const { closed, unclosedExpired } = unratedSessions.reduce(
      (acc, session) => {
        if (session.closed_at) {
          acc.closed.push(session);
        } else if (new Date(session.expires_at).getTime() < Date.now()) {
          acc.unclosedExpired.push(session);
        }
        return acc;
      },
      { closed: [], unclosedExpired: [] }
    );

    if (closeExpired && unclosedExpired.length > 0) {
      const closedNow = await this.closeSessions(unclosedExpired.map((session) => session.id));
      return [...closed, ...closedNow];
    } else {
      return closed;
    }
  };

  // !!! may create many unclosed sessions for the same chat mode
  private createSession = async (chatMode: ChatEntry): Promise<UserSession> => {
    const currentUserId = await this.getCurrentUserId();
    if (!currentUserId) {
      throw new Error("Not authenticated");
    }
    const { data, error } = await supabase
      .from("sessions")
      .insert({
        user_id: currentUserId,
        expires_at: new Date(Date.now() + SESSION_EXPIRATION_TIME_MS).toISOString(),
        chat_mode: chatMode,
      })
      .select()
      .single();
    if (error) throw new Error(error.message);
    return data;
  };

  /** Updates only the human-friendly `session_name` on a session row. */
  renameSession = async (
    sessionId: UserSession["id"],
    sessionName: string | null,
  ): Promise<UserSession> => {
    const trimmed = sessionName?.trim();
    const { data, error } = await supabase
      .from("sessions")
      .update({ session_name: trimmed && trimmed.length > 0 ? trimmed : null })
      .eq("id", sessionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  };

  /**
   * Hard-deletes a session and all its chat history rows (RLS-aware).
   * `session_chat_histories` does NOT cascade in schema, so we delete it explicitly first.
   */
  deleteSession = async (sessionId: UserSession["id"]): Promise<void> => {
    const { error: historyError } = await supabase
      .from("session_chat_histories")
      .delete()
      .eq("session_id", sessionId);
    if (historyError) throw historyError;

    const { error } = await supabase.from("sessions").delete().eq("id", sessionId);
    if (error) throw error;
  };

  closeSession = async (sessionId: UserSession["id"]): Promise<UserSession> => {
    const { data, error } = await supabase
      .from("sessions")
      .update({ closed_at: new Date().toISOString() })
      .eq("id", sessionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  };

  /**
   * Marks a previously closed session as active again (`closed_at = null`), extends TTL,
   * and closes any other open session for the same chat mode so only one stays open per mode.
   */
  reopenSession = async (sessionId: UserSession["id"]): Promise<UserSession> => {
    const existing = await this.getSessionById(sessionId);
    if (!existing) throw new Error("Session not found");

    const chatMode = existing.chat_mode;
    if (!existing.closed_at) {
      return existing;
    }

    const currentOpen = await this.getCurrentChatSession(chatMode);
    if (currentOpen && currentOpen.id !== sessionId) {
      await this.closeSession(currentOpen.id);
    }

    const { data, error } = await supabase
      .from("sessions")
      .update({
        closed_at: null,
        expires_at: new Date(Date.now() + SESSION_EXPIRATION_TIME_MS).toISOString(),
      })
      .eq("id", sessionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  };

  private closeSessions = async (sessionIds: UserSession["id"][]): Promise<UserSession[]> => {
    const { data, error } = await supabase
      .from("sessions")
      .update({ closed_at: new Date().toISOString() })
      .in("id", sessionIds)
      .select();

    if (error) throw error;
    return data;
  };

  private getCurrentUserId = async (): Promise<string | null> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.user?.id ?? null;
  };
}

type SessionHistoryJoinRow = UserSession & { session_chat_histories: { id: number }[] };

function stripHistoryJoinRow(row: SessionHistoryJoinRow): UserSession {
  const { session_chat_histories: _history, ...session } = row;
  return session;
}

export const sessionService = new SessionService();
